import { useMember } from "api/members";
import { useAppSelector } from "app/hooks";
import { UnlimitedValueWrapper } from "components/FormControls/UnlimitedValueWrapper";
import { AvailabilityColumn } from "components/Layout/Reward/RewardList/AvailabilityColumn/AvailabilityColumn";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useNavigate } from "react-router-dom";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { USER_AVAILABLE_REWARDS_LIST_ID } from "state/tables/types";
import { ListingActions } from "types/listing";
import { MemberReward } from "types/members";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

export function AvailableRewards({ memberID }: { memberID: string }) {
  const { message } = useMessage();
  const { useMemberAvailableRewards } = useMember(memberID);
  const { listing, updateListing } = useListing();
  const navigate = useNavigate();
  const defaultWallet = useAppSelector(selectDefaultWalletData);

  const { data: membersAvailableRewards } = useMemberAvailableRewards({
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
  });
  const { setColumnOrdering } = useTablesStateHelpers();

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      headerText: message({ id: "activeFrom" }),
      property: "activeFrom",
      isSortable: true,
    },
    {
      headerText: message({ id: "activeTo" }),
      property: "activeTo",
      isSortable: true,
    },
    {
      headerText: message({ id: "availability" }),
      property: "availability",
      isSortable: false,
      align: "right",
      tooltip: (
        <ExternalLinkTooltipContent
          name="availability"
          text={message({ id: "availabilityInfo" })}
        />
      ),
    },
    {
      align: "right",
      headerText: message(
        { id: "priceInUnits" },
        { units: defaultWallet?.unitPluralName },
      ),
      property: "costInPoints",
      isSortable: true,
      isVisible: false,
    },
    {
      align: "right",
      headerText: message({ id: "limit" }),
      property: "usageLimit:general",
      isSortable: true,
      isVisible: false,
    },
    {
      align: "right",
      headerText: message({ id: "limitPerMember" }),
      property: "usageLimit:perUser",
      isSortable: true,
      isVisible: false,
    },
  ]);

  function createItems(items: MemberReward[]): Row[] {
    return items.map((item) => ({
      cells: [
        {
          content: item.name,
          property: "name",
        },
        {
          content: <StatusValue status={item.active} />,
          property: "status",
        },
        {
          content: item.activity?.allTime
            ? message({ id: "isAllTimeActive" })
            : OLDate(item.activity?.from),
          property: "activeFrom",
        },
        {
          content: item.activity?.allTime
            ? message({ id: "isAllTimeActive" })
            : OLDate(item.activity?.to),
          property: "activeTo",
        },
        {
          align: "right",
          content: (
            <AvailabilityColumn
              usageLeft={item?.usageLeft}
              usageLimitGeneral={item?.usageLimit?.general}
              active={item?.active}
            />
          ),
          property: "availability",
        },
        { content: OLDate(item.createdAt), property: "createdAt" },
        {
          align: "right",
          content: item.costInPoints,
          emptyString: "0",
          property: "costInPoints",
        },
        {
          align: "right",
          content: <UnlimitedValueWrapper value={item.usageLimit?.general} />,
          emptyString: "0",
          property: "usageLimit:general",
        },
        {
          align: "right",
          content: <UnlimitedValueWrapper value={item.usageLimit?.perUser} />,
          emptyString: "0",
          property: "usageLimit:perUser",
        },
      ],
      id: item.rewardId,
      metadata: {
        disabled: !item.active,
      },
      testId: "available-rewards-table-row",
    }));
  }

  const items = membersAvailableRewards?.items
    ? createItems(membersAvailableRewards.items)
    : [];

  return (
    <OLTable
      setColumnOrdering={setColumnOrdering}
      tableName={USER_AVAILABLE_REWARDS_LIST_ID}
      actions={{
        body: [
          {
            label: message({ id: "seeDetails" }),
            action: (
              id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
            ) =>
              navigate(links.reward(id as string), {
                state: { prevPath: links.singleMemberRewards(memberID) },
              }),
          },
        ],
      }}
      data-test-id="single-member-available-rewards"
      columns={membersAvailableRewards && headItems}
      onSort={(prop) =>
        updateListing({
          payload: prop,
          type: ListingActions.SET_SORT,
        })
      }
      rows={membersAvailableRewards && items}
      sort={{
        order: listing.order || "asc",
        property: listing.property || "name",
        rowsPerPage: listing.rowsPerPage,
        page: listing.page,
      }}
      pagination={{
        count: calculateCount(
          membersAvailableRewards?.total.filtered,
          membersAvailableRewards?.total.all,
        ),
        page: listing.page,
        onChangePage: (page) => {
          updateListing({
            payload: page,
            type: ListingActions.CHANGE_PAGE,
          });
        },
        onChangeRowsPerPage: (rows) => {
          updateListing({
            payload: rows,
            type: ListingActions.CHANGE_ROWS_PER_PAGE,
          });
        },
        rowsPerPage: listing.rowsPerPage,
      }}
    />
  );
}
